import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  useChangeClientMutation,
  useLazyGetClientsQuery,
  useLazyGetConsultationsQuery,
} from '../services/users.api';
import { ActiveClients } from '../components/journal/active-clients';
import { ArchiveClients } from '../components/journal/archive-clients';
import { CreateClientForm, IFormDataClient } from '../components/create-client-form';
import { IClient } from '../store/types';
import { usePosthogCapture } from '../hooks/usePosthogCapture';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { Breakpoint } from '../utils/breakpoint';

import { JournalMobile } from './journal/journal-mobile';

const tabs = [
  { id: 1, title: 'Активные клиенты' },
  { id: 2, title: 'Архивные клиенты' },
  { id: 3, title: 'Групповая терапия' },
];

import { resolveJournalOverviewTab, type JournalLocationState } from './journal/journal-navigation';

export type { JournalLocationState };

const normalizeId = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const Journal = () => {
  const [getClients, { data: clients }] = useLazyGetClientsQuery();
  const [getConsultations, { data: consultations }] = useLazyGetConsultationsQuery();
  const [changeClient] = useChangeClientMutation();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = (location.state as JournalLocationState | null) ?? null;
  const normalizedClientId = normalizeId(locationState?.clientId ?? null);
  const initialActiveTab = resolveJournalOverviewTab(locationState);
  const targetClientIdRef = useRef<number | null>(normalizedClientId);
  const consumedNavigationRef = useRef(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  const [activeClient, setActiveClient] = useState<number | null>(null);
  const [pendingSessionDetail, setPendingSessionDetail] = useState<{
    consultationId: number;
    initialTab: string;
  } | null>(() => locationState?.openSessionDetail ?? null);
  const [isSessionDetailMode, setIsSessionDetailMode] = useState(false);
  const [isEditClientOpen, setIsEditClientOpen] = useState(false);
  const [editedClient, setEditedClient] = useState<IClient | null>(null);
  const captureEvent = usePosthogCapture();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint < Breakpoint.Md;

  const fetchClients = useCallback(() => {
    if (activeTab === 2) {
      getClients({ sort: 'DESC', is_active: false, full_name: search });
    } else {
      getClients({ sort: 'DESC', is_active: true, full_name: search });
    }
  }, [activeTab, getClients, search]);

  const filteredClients = useMemo(() => {
    const list = clients ?? [];
    if (activeTab === 1) return list.filter((c) => !c.is_group);
    if (activeTab === 2) return list.filter((c) => !c.is_group);
    if (activeTab === 3) return list.filter((c) => c.is_group === true);
    return list;
  }, [clients, activeTab]);

  const handleEditClient = useCallback((client: IClient) => {
    setEditedClient(client);
    setIsEditClientOpen(true);
  }, []);

  const handleCloseEditClient = useCallback(() => {
    setIsEditClientOpen(false);
    setEditedClient(null);
  }, []);

  useEffect(() => {
    if (clients === undefined) return;
    if (filteredClients.length === 0) {
      targetClientIdRef.current = null;
      setActiveClient(null);
      return;
    }

    const targetId = normalizeId(targetClientIdRef.current);
    if (targetId !== null) {
      const match = filteredClients.find((client) => normalizeId(client.id) === targetId);
      if (match) {
        targetClientIdRef.current = null;
        setActiveClient(normalizeId(match.id));
      }
      return;
    }

    const currentActive = normalizeId(activeClient);
    if (currentActive !== null && filteredClients.some((client) => normalizeId(client.id) === currentActive)) {
      return;
    }

    setActiveClient(normalizeId(filteredClients[0]?.id ?? null));
  }, [clients, filteredClients, activeClient]);

  useEffect(() => {
    if (consumedNavigationRef.current || !locationState?.openSessionDetail) {
      return;
    }

    consumedNavigationRef.current = true;
    setPendingSessionDetail(locationState.openSessionDetail);

    if (locationState.clientId !== undefined) {
      targetClientIdRef.current = normalizeId(locationState.clientId);
      setActiveTab(resolveJournalOverviewTab(locationState));
    } else if (locationState.journalTabId !== undefined) {
      setActiveTab(resolveJournalOverviewTab(locationState));
    }

    navigate(location.pathname, {
      replace: true,
      state:
        locationState.clientId !== undefined
          ? { clientId: locationState.clientId, isClientActive: locationState.isClientActive }
          : {},
    });
  }, [location.pathname, locationState, navigate]);

  useEffect(() => {
    if (activeClient) {
      getConsultations({
        client_entity: `/api/v1/clients/${activeClient}`,
        'sort[created_at]': 'desc',
      });
    }
  }, [activeClient, getConsultations]);

  useEffect(() => {
    if (!activeClient || !pendingSessionDetail) {
      return;
    }

    getConsultations({
      client_entity: `/api/v1/clients/${activeClient}`,
      'sort[created_at]': 'desc',
    });
  }, [activeClient, pendingSessionDetail, getConsultations]);

  const getTab = () => {
    switch (activeTab) {
      case 1:
        return (
          <ActiveClients
            consultations={activeClient ? consultations || [] : []}
            activeClient={activeClient}
            setActiveClient={setActiveClient}
            search={search}
            setSearch={setSearch}
            clients={filteredClients}
            onEditClient={handleEditClient}
            onSessionDetailModeChange={setIsSessionDetailMode}
            pendingSessionDetail={pendingSessionDetail}
            onPendingSessionDetailHandled={() => setPendingSessionDetail(null)}
          />
        );
      case 2:
        return (
          <ArchiveClients
            consultations={activeClient ? consultations || [] : []}
            activeClient={activeClient}
            setActiveClient={setActiveClient}
            search={search}
            setSearch={setSearch}
            clients={filteredClients}
            onEditClient={handleEditClient}
            onSessionDetailModeChange={setIsSessionDetailMode}
            pendingSessionDetail={pendingSessionDetail}
            onPendingSessionDetailHandled={() => setPendingSessionDetail(null)}
          />
        );
      case 3:
        return (
          <ActiveClients
            consultations={activeClient ? consultations || [] : []}
            activeClient={activeClient}
            setActiveClient={setActiveClient}
            search={search}
            setSearch={setSearch}
            clients={filteredClients}
            onEditClient={handleEditClient}
            onSessionDetailModeChange={setIsSessionDetailMode}
            pendingSessionDetail={pendingSessionDetail}
            onPendingSessionDetailHandled={() => setPendingSessionDetail(null)}
            isGroupTherapy
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    setIsSessionDetailMode(false);
  }, [activeTab]);

  useEffect(() => {
    if (location.pathname.split('/').length === 2) {
      captureEvent('journal_overview_viewed');
    }
  }, [captureEvent, location.pathname]);

  const handleSaveClient = async (formDataClient: IFormDataClient) => {
    if (!editedClient) {
      return;
    }

    try {
      await changeClient({
        id: editedClient.id,
        full_name: formDataClient.name,
        time_zone: formDataClient.tz,
        is_active: formDataClient.active,
      }).unwrap();
      toast.success(editedClient.is_group ? 'Встреча успешно изменена' : 'Клиент успешно изменён');
      fetchClients();
    } catch (error) {
      toast.error(editedClient.is_group ? 'Не удалось изменить встречу' : 'Не удалось изменить клиента');
    }
  };

  const isOverview = location.pathname.split('/').length === 2;

  return (
    <div className="w-full min-w-0 min-h-full overflow-x-hidden bg-grey5">
      {isOverview &&
        (isMobile ? (
          <JournalMobile
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            search={search}
            setSearch={setSearch}
            clients={filteredClients}
            consultations={activeClient ? consultations || [] : []}
            activeClient={activeClient}
            setActiveClient={setActiveClient}
            onEditClient={handleEditClient}
            pendingSessionDetail={pendingSessionDetail}
            onOpenSessionDetail={(consultationId, initialTab) =>
              setPendingSessionDetail({ consultationId, initialTab })
            }
            onPendingSessionDetailHandled={() => setPendingSessionDetail(null)}
          />
        ) : (
          <div className="w-full min-w-0 px-6 pb-[85px] pt-6">
            {!isSessionDetailMode && (
              <h2 className="font-comfortaa font-semibold text-lg sm:text-2xl text-blackBase mb-5">
                Терапевтический журнал
              </h2>
            )}

            {!isSessionDetailMode && (
              <div className="flex gap-6 border-b border-gray-200 font-comfortaa mb-5 overflow-x-auto">
                {tabs.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActiveTab(item.id)}
                    className={classNames(
                      'py-1 px-1 lg:px-6 font-medium sm:text-sm border-b-2 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-redBase focus-visible:ring-offset-2',
                      {
                        'border-red-500 text-[#C00000]': item.id === activeTab,
                        'border-transparent text-gray-500 hover:border-b-[#DEC05E]': item.id !== activeTab,
                      },
                    )}
                  >
                    {item.title}
                  </button>
                ))}
              </div>
            )}

            {getTab()}
          </div>
        ))}

      <CreateClientForm
        clientData={editedClient ?? undefined}
        open={isEditClientOpen}
        close={handleCloseEditClient}
        save={handleSaveClient}
      />
    </div>
  );
};

export default Journal;
